import type z from 'zod/v4'
import { relations, sql } from 'drizzle-orm'
import { index, integer, pgTable, timestamp, unique, uuid } from 'drizzle-orm/pg-core'
import { createInsertSchema, createSelectSchema, createUpdateSchema } from 'drizzle-zod'

import { BookCollection } from '../../content'

// =============================================================================
// BOOK COLLECTION METRICS MONTHLY - Lookup table (`cron / metrics-monthly`)
// Grain: one row per (bookCollectionId, month=start-of-month UTC).
// Recomputed each run from LoveBookCollection / BookCollectionView event rows
// (createdAt >= month), UPSERT target [bookCollectionId, month]. Idempotent.
// =============================================================================

export const BookCollectionMetricsMonthly = pgTable(
  'book_collection_metrics_monthly',
  {
    id: uuid().defaultRandom().primaryKey(),
    bookCollectionId: uuid()
      .references(() => BookCollection.id, { onDelete: 'cascade' })
      .notNull(),
    month: timestamp({ mode: 'date', withTimezone: true }).notNull(),
    likesCount: integer().notNull().default(0),
    viewsCount: integer().notNull().default(0),
    calculatedAt: timestamp({ mode: 'date', withTimezone: true }).defaultNow().notNull(),
    createdAt: timestamp({ mode: 'date', withTimezone: true }).defaultNow().notNull(),
    updatedAt: timestamp({ mode: 'date', withTimezone: true }).$onUpdateFn(() => sql`now()`),
  },
  (t) => [
    index('idx_book_collection_metrics_monthly_collection').on(t.bookCollectionId),
    index('idx_book_collection_metrics_monthly_month').on(t.month),
    index('idx_book_collection_metrics_monthly_month_likes').on(t.month, t.likesCount),
    index('idx_book_collection_metrics_monthly_month_views').on(t.month, t.viewsCount),
    unique('uq_book_collection_metrics_monthly_collection_month').on(t.bookCollectionId, t.month),
  ],
)

export const bookCollectionMetricsMonthlyRelations = relations(BookCollectionMetricsMonthly, ({ one }) => ({
  bookCollection: one(BookCollection, {
    fields: [BookCollectionMetricsMonthly.bookCollectionId],
    references: [BookCollection.id],
  }),
}))

export const InsertBookCollectionMetricsMonthlySchema = createInsertSchema(BookCollectionMetricsMonthly).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
})
export const UpdateBookCollectionMetricsMonthlySchema = createUpdateSchema(BookCollectionMetricsMonthly)
  .partial()
  .omit({
    id: true,
    bookCollectionId: true,
    month: true,
    createdAt: true,
    updatedAt: true,
  })
export const SelectBookCollectionMetricsMonthlySchema = createSelectSchema(BookCollectionMetricsMonthly)

export type InsertBookCollectionMetricsMonthly = typeof BookCollectionMetricsMonthly.$inferInsert
export type UpdateBookCollectionMetricsMonthly = z.infer<typeof UpdateBookCollectionMetricsMonthlySchema>
export type SelectBookCollectionMetricsMonthly = typeof BookCollectionMetricsMonthly.$inferSelect
